import dayjs from "dayjs";
import styles from "../MoveOutChecklist.module.css";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { PhaseBanner } from "./CommonComponents";
import RentTypeSwitch from "./RentTypeSwitch";
import FinalStatementBreakdown, { currency } from "./FinalStatementBreakdown";

/*
 * All amounts come from the backend statement (v1/moveout-payment-data), which is the
 * same calculation that gets posted to the ledger, PDF and email on submit.
 * Sign convention: positive = tenant owes, negative = refund to tenant.
 */
const StatementTab = ({
    moveoutData,
    submitMoveoutChceckList,
    setTab
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [rentType, setRentType] = useState(moveoutData?.final_month_rent_type || "prorated");
    const [statement, setStatement] = useState(null);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        const fetchStatement = async () => {
            setFetching(true);
            try {
                const token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE
                });

                const response = await axios.get(
                    process.env.REACT_APP_PAYMENT_API_URL +
                    "v1/moveout-payment-data/" +
                    moveoutData?.tenant_id,
                    {
                        params: { rent_type: rentType },
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }
                );

                setStatement(response.data.data);
            } catch (error) {
                console.error("Failed to fetch move-out statement:", error);
            }
            setFetching(false);
        };

        if (moveoutData?.tenant_id) {
            fetchStatement();
        }
    }, [moveoutData?.tenant_id, rentType, getAccessTokenSilently]);

    const finalBalance = parseFloat(statement?.final_balance) || 0;
    const isRefund = finalBalance < 0;
    const isDue = finalBalance > 0;

    const onSubmit = () => {
        submitMoveoutChceckList({
            is_submitted: 1,
            is_completed: 1,
            current_step: 4,
            final_month_rent_type: statement?.is_full_stay ? "full" : rentType,
            final_amount: finalBalance
        });
    };

    return (
        <>
            <div className={`${styles.tp} ${styles.tpActive}`}>

                <PhaseBanner
                    type="green"
                    icon="receipt"
                    title="Final Statement — Ready to send"
                    sub={`Inspection on ${dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}. Review the settlement and send the final statement to the tenant.`}
                />

                <FinalStatementBreakdown
                    statement={statement}
                    moveoutDate={moveoutData?.moveout_date}
                    voidedPending
                    rentTypeSwitch={
                        <RentTypeSwitch
                            value={rentType}
                            onChange={setRentType}
                            statement={statement}
                            disabled={fetching}
                        />
                    }
                />

                {/* =========================
                    VERDICT
                ========================= */}
                {statement && (
                    <div className={`${styles.verdict} ${isDue ? styles.verdictOwe : styles.verdictRefund}`}>
                        <i className={`ti ${isDue ? "ti-alert-circle" : "ti-circle-check"} ${styles.verdictIcon}`} />

                        <div>
                            <div className={styles.vt}>
                                {isRefund
                                    ? `Refund of ${currency(finalBalance)}`
                                    : isDue
                                        ? `Collect balance of ${currency(finalBalance)}`
                                        : "Account settled"}
                            </div>

                            <div className={styles.vs}>
                                {isRefund
                                    ? "Post credit to tenant ledger. Transfer refund within 30 days of move-out."
                                    : isDue
                                        ? "Raise final invoice. Collect before releasing deposit remainder."
                                        : "No further payment or refund is required."}
                            </div>
                        </div>
                    </div>
                )}

                {/* =========================
                    ACTIONS
                ========================= */}
                <div className={styles.fa}>
                    <button
                        className={`${styles.btn} ${styles.btnS}`}
                        onClick={() => {
                            setTab((p) => p - 1);
                        }}
                    >
                        <i className="ti ti-arrow-left" /> Back
                    </button>

                    <div className={styles.faRight}>
                        <button className={`${styles.btn} ${styles.btnS}`}>
                            Cancel
                        </button>

                        <button
                            className={`${styles.btn} ${styles.btnP}`}
                            onClick={onSubmit}
                            disabled={!statement || fetching}
                        >
                            <i className="ti ti-send" /> Send final statement
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StatementTab;
